import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Scale } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ago = (iso) => {
  const h = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 3600000));
  return h < 24 ? `${h}h` : `${Math.round(h / 24)}d`;
};

export const RebalanceDialog = ({ collector, onClose, onDone }) => {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!collector) return;
    setPlan(null);
    api.get(`/company/workload/${collector.id}/rebalance`).then(({ data }) => setPlan(data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); onClose(); });
  }, [collector, onClose]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/company/workload/${collector.id}/rebalance`);
      toast.success(`Moved ${data.moved} lead${data.moved === 1 ? "" : "s"} from ${collector.name}${data.failed ? ` · ${data.failed} couldn't be moved` : ""}`);
      onClose();
      await onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!collector} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rebalance-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#1E3F33] flex items-center gap-2"><Scale className="w-5 h-5 text-[#CC5A3A]" /> Rebalance {collector?.name}</DialogTitle>
          <DialogDescription>{plan ? `${plan.collector.open} open leads · team average ${plan.team_average}. Each lead goes to its best-matched teammate.` : "Working out the best moves…"}</DialogDescription>
        </DialogHeader>
        {plan && (plan.moves.length === 0 ? (
          <p className="text-sm text-[#8A9A86]" data-testid="rebalance-empty">No moves available — no teammate who offers these services and covers the area has room right now.{plan.skipped ? ` (${plan.skipped} lead${plan.skipped === 1 ? "" : "s"} had no suitable teammate.)` : ""}</p>
        ) : (
          <div>
            <ul className="max-h-72 overflow-auto rounded-xl border border-[#1E3F33]/10 divide-y divide-[#1E3F33]/8 text-sm" data-testid="rebalance-moves">
              {plan.moves.map((m) => (
                <li key={m.lead_id} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`rebalance-move-${m.lead_id}`}>
                  <span className="min-w-0">
                    <span className="block font-medium text-[#1E3F33] truncate">{m.customer_name} · {m.services.join(", ")}</span>
                    <span className="block text-[11px] text-[#8A9A86]">{m.zip_codes.join(", ")} · waiting {ago(m.waiting_since)}</span>
                  </span>
                  <span className="text-xs text-[#2D6A4F] font-medium whitespace-nowrap">→ {m.to_name}</span>
                </li>
              ))}
            </ul>
            {plan.skipped > 0 && <p className="text-[11px] text-[#8A9A86] mt-2">{plan.skipped} other lead{plan.skipped === 1 ? "" : "s"} stay — no suitable teammate.</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} className="btn-secondary text-sm py-2 px-4" data-testid="rebalance-cancel">Cancel</button>
              <button onClick={confirm} disabled={busy} className="btn-primary text-sm py-2 px-4 disabled:opacity-50" data-testid="rebalance-confirm">{busy ? "Moving…" : `Move ${plan.moves.length} lead${plan.moves.length === 1 ? "" : "s"}`}</button>
            </div>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};
